import { useEffect, useRef, useSyncExternalStore } from 'react'
import { rfStore } from '../lib/rfstore'
import { PTT } from './PTT'
import { RXReadout } from './RXReadout'
import { CWReadout } from './CWReadout'
import { CWSender } from './CWSender'
import { FreqReadout } from './FreqReadout'
import { SMeter } from './SMeter'
import { DialStrip, type DialPresence, type DialSegment, type DialRepeater } from './DialStrip'
import type { Band } from '../lib/client'
import { useI18n } from '../lib/i18n'

/**
 * Modos que dão pra escolher no ciclo do dock. A checagem de verdade — se a
 * classe/faixa/potência permitem aquele modo ali — é sempre do SERVIDOR, no
 * gate de TX; o botão só oferece a escolha, e uma tentativa inválida volta
 * como negativa explicada (`dock-bloqueio`), igual a qualquer outra recusa.
 */
const MODOS = ['SSB', 'FM', 'AM', 'CW'] as const

/** Larguras de filtro de FI — chaves fixas, nomes traduzidos no componente. */
export const FILTRO_HZ = [3000, 2400, 1800, 500] as const

/** Níveis de squelch: null é desligado. Os outros são o limiar de SNR em dB
 * abaixo do qual o canal fica em silêncio. "Médio" é o padrão — ligado. */
export const SQUELCH_DB = [null, -3, 0, 6] as const

/**
 * Dock de operação: tudo que se usa DURANTE um QSO, fixo no rodapé.
 *
 * A regra de ouro do docs/06 é "o polegar é o operador" — e a interface
 * anterior a quebrava de forma grosseira: o PTT ficava a 1400 px do topo,
 * abaixo do exame, do logbook e da loja, e a sintonia ficava em outro lugar
 * ainda. Para falar era preciso rolar a página.
 *
 * Aqui os quatro controles do QSO — sintonia, banda, leitura de sinal e PTT —
 * ocupam o mesmo bloco fixo, na altura em que o polegar chega sem a mão sair
 * da posição. Nada aqui rola.
 */
export function Dock({
  band,
  bands,
  segments,
  presences,
  repeaters,
  repeaterAtual,
  filterHz,
  squelchDb,
  ruidoLigado,
  scanning,
  aberta = false,
  onTune,
  onStep,
  onBand,
  onFilterChange,
  onSquelchChange,
  onRuidoChange,
  onScanToggle,
}: {
  band: Band | null
  bands: Band[]
  segments: DialSegment[]
  presences: DialPresence[]
  repeaters: DialRepeater[]
  /** Repetidora cuja saída está sob a agulha agora, se houver. */
  repeaterAtual: DialRepeater | null
  filterHz: number
  squelchDb: number | null
  /** "Áudio limpo": chiado de banda ligado (padrão) ou desligado. */
  ruidoLigado: boolean
  scanning: boolean
  aberta?: boolean
  onTune: (khz: number, modo?: string, imediato?: boolean) => void
  onStep: (delta: number) => void
  onBand: (b: Band) => void
  onFilterChange: (hz: number) => void
  onSquelchChange: (db: number | null) => void
  onRuidoChange: (on: boolean) => void
  onScanToggle: () => void
}) {
  const { t } = useI18n()
  const FILTROS = [
    { hz: FILTRO_HZ[0], nome: t.dock.filtroLargo },
    { hz: FILTRO_HZ[1], nome: t.dock.filtroNormal },
    { hz: FILTRO_HZ[2], nome: t.dock.filtroEstreito },
    { hz: FILTRO_HZ[3], nome: t.dock.filtroCW },
  ] as const
  const SQUELCH_NIVEIS = [
    { db: SQUELCH_DB[0], nome: t.dock.squelchDesligado },
    { db: SQUELCH_DB[1], nome: t.dock.squelchBaixo },
    { db: SQUELCH_DB[2], nome: t.dock.squelchMedio },
    { db: SQUELCH_DB[3], nome: t.dock.squelchAlto },
  ] as const

  const state = useSyncExternalStore(rfStore.subscribe, () => rfStore.snapshot())
  const podeTx = state.channel?.can_transmit ?? false

  // Qual canal da PX está sob a agulha.
  const canal = aberta
    ? segments.find(
        (s) => state.freqKHz >= s.low_khz && state.freqKHz <= s.high_khz,
      )?.channel
    : undefined

  // A fileira de bandas rola na horizontal, e a banda em uso pode estar fora da
  // vista — 2 m fica no fim de uma lista que começa em 160 m. Ver em que banda
  // se está exige então rolar, o que anula o ponto de ter a fileira.
  const selRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    selRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' })
  }, [band?.id])

  const filtro = FILTROS.find((f) => f.hz === filterHz) ?? FILTROS[1]
  const proximoFiltro = () => {
    const i = FILTROS.findIndex((f) => f.hz === filterHz)
    const prox = FILTROS[(i + 1) % FILTROS.length] ?? FILTROS[0]
    onFilterChange(prox.hz)
  }

  const squelch = SQUELCH_NIVEIS.find((s) => s.db === squelchDb) ?? SQUELCH_NIVEIS[0]
  const proximoSquelch = () => {
    const i = SQUELCH_NIVEIS.findIndex((s) => s.db === squelchDb)
    const prox = SQUELCH_NIVEIS[(i + 1) % SQUELCH_NIVEIS.length] ?? SQUELCH_NIVEIS[0]
    onSquelchChange(prox.db)
  }

  const modoAtual = state.channel?.mode ?? 'SSB'
  const proximoModo = () => {
    const i = MODOS.indexOf(modoAtual as (typeof MODOS)[number])
    const prox = MODOS[(i + 1) % MODOS.length] ?? MODOS[0]
    onTune(state.freqKHz, prox, true)
  }

  return (
    <div className="dock">
      <DialStrip
        band={band}
        segments={segments}
        presences={presences}
        repeaters={repeaters}
        aberta={aberta}
        onTune={onTune}
      />

      <div className="dock-leitura">
        <div className="dock-freq">
          <FreqReadout />
          <span className="dock-unid">
            MHz {state.channel?.mode ?? ''}
            {/* Na PX o canal é o endereço; a frequência é detalhe técnico. */}
            {canal && <b className="canal">{t.dock.canal} {canal}</b>}
          </span>
        </div>
        <div className="dock-sinal">
          <SMeter segments={14} />
          <RXReadout />
        </div>
      </div>

      <div className="dock-extra">
        <button
          className={modoAtual === 'CW' ? 'dock-modo cw' : 'dock-modo'}
          onClick={proximoModo}
          aria-label={`${t.dock.modoLabel} ${modoAtual} ${t.dock.toqueParaTrocar}`}
        >
          {modoAtual}
        </button>
        <button
          className="dock-filtro"
          onClick={proximoFiltro}
          aria-label={`${t.dock.filtroFI} ${filtro.nome}, ${filtro.hz} Hz ${t.dock.toqueParaTrocar}`}
        >
          {filtro.nome} <small>{filtro.hz >= 1000 ? `${filtro.hz / 1000}k` : filtro.hz}</small>
        </button>
        <button
          className={squelchDb === null ? 'dock-squelch' : 'dock-squelch ligado'}
          onClick={proximoSquelch}
          aria-label={`${t.dock.squelchLabel} ${squelch.nome} ${t.dock.toqueParaTrocar}`}
        >
          {t.dock.sql} <small>{squelch.nome}</small>
        </button>
        <button
          className={ruidoLigado ? 'dock-ruido' : 'dock-ruido limpo'}
          onClick={() => onRuidoChange(!ruidoLigado)}
          aria-label={
            ruidoLigado
              ? t.dock.chiadoLigado
              : t.dock.audioLimpo
          }
        >
          {ruidoLigado ? t.dock.chiado : t.dock.limpo}
        </button>
        <button
          className={scanning ? 'dock-varrer varrendo' : 'dock-varrer'}
          onClick={onScanToggle}
        >
          {scanning ? t.dock.parar : t.dock.varrer}
        </button>
        {/* A repetidora acesa aqui é a mesma marcada na fita — o mesmo dado,
            só que legível sem precisar decifrar um losango. */}
        {repeaterAtual && (
          <span className={repeaterAtual.can_access ? 'pill on' : 'pill fraco'}>
            {/* Nem toda repetidora do catálogo tem indicativo cadastrado —
                cidade ainda identifica qual é, em vez de deixar um "· FM" solto. */}
            {repeaterAtual.callsign || repeaterAtual.city} · {repeaterAtual.mode}
          </span>
        )}
      </div>

      {/* Por que o PTT está apagado, na altura do olho de quem vai apertá-lo.
          Antes isso só existia no rodapé do botão, no fim da página. */}
      {state.connected && state.channel && !podeTx && (
        <p className="dock-bloqueio">
          {state.channel.deny_reason ?? t.dock.semPrivilegio}
        </p>
      )}

      <div className="dock-passo">
        <button onClick={() => onStep(-5)} aria-label={t.dock.descer5}>−5</button>
        <button onClick={() => onStep(-0.5)} aria-label={t.dock.descerMeio}>−</button>
        <div className="dock-bandas" role="group" aria-label={t.dock.bandasAriaLabel}>
          {bands.map((b) => (
            <button
              key={b.id}
              ref={band?.id === b.id ? selRef : undefined}
              // Faixa aberta ganha o verde do olho mágico: é onde QUALQUER um
              // pode falar, e essa diferença precisa ser visível antes do toque.
              className={
                [b.open_to_all ? 'aberta' : '', band?.id === b.id ? 'sel' : '']
                  .filter(Boolean)
                  .join(' ')
              }
              onClick={() => onBand(b)}
            >
              {b.id}
            </button>
          ))}
        </div>
        <button onClick={() => onStep(0.5)} aria-label={t.dock.subirMeio}>+</button>
        <button onClick={() => onStep(5)} aria-label={t.dock.subir5}>+5</button>
      </div>

      {modoAtual === 'CW' ? (
        <>
          <CWReadout />
          <CWSender powerW={100} />
        </>
      ) : (
        <PTT powerW={100} />
      )}
    </div>
  )
}
